/**
 * Device, browser and locality facts, read once per analytics session.
 *
 * ## Why the parsing is here and not on the server
 *
 * The server already stores the raw `user-agent` header in its own column on
 * every event — see `events_controller.ex`. So this module is not the only record
 * of what the browser said: it is a *derived, queryable* view of it. If bowser
 * ever mis-parses something, the raw string is still on the row and the answer
 * can be recomputed. That is what makes client-side parsing safe here.
 *
 * The corollary: never put `navigator.userAgent` in the payload. It is already
 * stored, and duplicating it into `properties` costs bytes against a 4 KB ceiling
 * to say the same thing twice.
 *
 * ## Locality is timezone and locale, nothing more
 *
 * No Geolocation API — it would prompt for permission on session start, be
 * denied by most people, and record the precise position of individuals to answer
 * a question that "which timezone" already answers. No IP lookup either: the
 * server has the IP if country is ever wanted, so that stays a server-side
 * decision rather than something this file leaks into the payload.
 *
 * ## Nothing here may throw
 *
 * This runs inside `getSessionId()`, which runs inside `buildPageViewEvent()`.
 * A throw would take out page-view tracking as collateral, so every browser API
 * touched below is treated as absent-or-hostile.
 */

import Bowser from "bowser";

import type { DeviceType, SessionStartProperties } from "./types";

/**
 * `navigator.languages` is unbounded and `properties` has a hard 4 KB server-side
 * ceiling. Five is past the point of diminishing information.
 */
const MAX_LANGUAGES = 5;

/**
 * bowser also emits `bot`, `xr`, `embedded` and `wearable`. Those are real but not
 * things this product reports on, and letting them through would widen a column
 * that dashboards group by. Anything unrecognised becomes `unknown`.
 */
const KNOWN_TYPES = new Set<string>(["mobile", "tablet", "desktop", "tv"]);

const nonEmpty = (value: string | undefined): string | null => value || null;

const attempt = <T>(read: () => T, fallback: T): T => {
  try {
    const value = read();
    return value === undefined || value === null ? fallback : value;
  } catch {
    return fallback;
  }
};

/**
 * How many simultaneous touches the device reports.
 *
 * Load-bearing beyond curiosity: it is the only way to tell an iPad from a Mac.
 * See `resolveDeviceType`.
 */
const touchPoints = (): number => attempt(() => window.navigator.maxTouchPoints ?? 0, 0);

/**
 * iPadOS 13 and later report a **desktop macOS user agent** in Safari. bowser
 * believes it, as it must — the string genuinely says Macintosh — so every iPad
 * would land in the data as a desktop.
 *
 * `maxTouchPoints` is the tell: an iPad reports 5, a Mac reports 0, and a Mac with
 * a Touch Bar or a touch-capable trackpad can report 1. Hence `> 1` rather than
 * `> 0`.
 *
 * Note what this deliberately does not attempt: laptop versus desktop. No browser
 * exposes chassis type, and screen size is not a proxy for it — a 27" monitor and
 * a docked laptop are indistinguishable. Both are `desktop`.
 */
const resolveDeviceType = (
  parsedType: string | undefined,
  osName: string | undefined,
  points: number,
): DeviceType => {
  if (parsedType === "desktop" && osName === "macOS" && points > 1) return "tablet";
  if (parsedType && KNOWN_TYPES.has(parsedType)) return parsedType as DeviceType;
  return "unknown";
};

/**
 * Minutes east of UTC — India is `330`, New York is `-300`.
 *
 * The sign is flipped from `getTimezoneOffset()`, which returns minutes *west*
 * and therefore reads backwards to everyone who has not just looked it up.
 */
const utcOffsetMinutes = (): number => attempt(() => -new Date().getTimezoneOffset(), 0);

export const describeDevice = (): SessionStartProperties => {
  const points = touchPoints();
  // `Bowser.parse` on a UA it cannot place still returns the full shape with
  // undefined leaves, so there is no partial-object case to handle — but an absent
  // `navigator` in a non-browser context would throw before that.
  const parsed = attempt(
    () => Bowser.parse(window.navigator.userAgent),
    {} as Bowser.Parser.ParsedResult,
  );

  return {
    browser: {
      name: nonEmpty(parsed.browser?.name),
      version: nonEmpty(parsed.browser?.version),
    },
    os: {
      name: nonEmpty(parsed.os?.name),
      version: nonEmpty(parsed.os?.version),
    },
    device: {
      type: resolveDeviceType(parsed.platform?.type, parsed.os?.name, points),
      vendor: nonEmpty(parsed.platform?.vendor),
      model: nonEmpty(parsed.platform?.model),
    },
    engine: { name: nonEmpty(parsed.engine?.name) },

    timezone: attempt(() => Intl.DateTimeFormat().resolvedOptions().timeZone || null, null),
    utc_offset_minutes: utcOffsetMinutes(),
    locale: attempt(() => window.navigator.language || null, null),
    languages: attempt(
      () => Array.from(window.navigator.languages ?? []).slice(0, MAX_LANGUAGES),
      [],
    ),

    // Screen, not viewport: the paired `page_view` already carries the viewport,
    // and screen size is the part that says something about the hardware.
    screen: {
      w: attempt(() => window.screen.width, 0),
      h: attempt(() => window.screen.height, 0),
      dpr: attempt(() => window.devicePixelRatio, 1),
    },
    touch_points: points,
    pointer_coarse: attempt(() => window.matchMedia("(pointer: coarse)").matches, false),
  };
};
